import { aspectRatio } from "./templates";

// Spacing used by auto-arrange, in percent of the board width (as templates).
const MARGIN = 3;
const GAP = 1.4;
// Below this share of the board, auto-arrange uses the whole board instead of
// the area the images already occupy.
const MIN_REGION = 0.15;

const round = (value) => Math.round(value * 1000) / 1000;

export const frameAspect = (item, ratio) => (item.w * ratio) / item.h;

// Same maths as image_geometry() in api/moodboard_render.py: the image's size
// and offset as percentages of its frame.
export function imageGeometry(item, imageW, imageH, ratio) {
  const boxAspect = frameAspect(item, ratio);
  const imageAspect = imageW / imageH;
  const wider = imageAspect > boxAspect;
  let relW;
  let relH;
  if (item.fit === "contain") {
    [relW, relH] = wider ? [1, boxAspect / imageAspect] : [imageAspect / boxAspect, 1];
  } else {
    [relW, relH] = wider ? [imageAspect / boxAspect, 1] : [1, boxAspect / imageAspect];
  }
  const zoom = item.zoom || 1;
  relW *= zoom;
  relH *= zoom;
  return {
    width: relW * 100,
    height: relH * 100,
    left: (1 - relW) * (item.focus_x ?? 50),
    top: (1 - relH) * (item.focus_y ?? 50),
  };
}

export const FULL_CROP = { x: 0, y: 0, w: 1, h: 1 };
export const cropOf = (item) => item.crop || FULL_CROP;

// Where the whole <img> sits so that its cropped part lands where
// imageGeometry() puts it. A flipped image is mirrored about its own centre, so
// the mirrored crop is the part that must line up. Matches _paste_image().
export function imagePlacement(item, imageW, imageH, ratio) {
  const crop = cropOf(item);
  const inner = imageGeometry(item, imageW * crop.w, imageH * crop.h, ratio);
  const width = inner.width / crop.w;
  const height = inner.height / crop.h;
  const cropX = item.flip_x ? 1 - crop.x - crop.w : crop.x;
  const cropY = item.flip_y ? 1 - crop.y - crop.h : crop.y;
  return { width, height, left: inner.left - cropX * width, top: inner.top - cropY * height };
}

// The image's shape once cropped.
export const croppedAspect = (item, imageW, imageH) => {
  const crop = cropOf(item);
  return (imageW * crop.w) / (imageH * crop.h);
};

// Same order and defaults as _adjust_matrices() in the renderer.
export const ADJUSTMENTS = [
  { key: "brightness", label: "Brightness", min: 50, max: 150, neutral: 100 },
  { key: "contrast", label: "Contrast", min: 50, max: 150, neutral: 100 },
  { key: "saturation", label: "Saturation", min: 0, max: 200, neutral: 100 },
  { key: "sepia", label: "Warmth", min: 0, max: 100, neutral: 0 },
];
const CSS_FILTERS = { brightness: "brightness", contrast: "contrast", saturation: "saturate", sepia: "sepia" };

export function imageFilter(item) {
  const parts = ADJUSTMENTS.filter(({ key, neutral }) => (item[key] ?? neutral) !== neutral).map(
    ({ key }) => `${CSS_FILTERS[key]}(${item[key] / 100})`
  );
  return parts.length ? parts.join(" ") : undefined;
}

export const isAdjusted = (item) => ADJUSTMENTS.some(({ key, neutral }) => (item[key] ?? neutral) !== neutral);

// Keeps an angle in (-180, 180].
export const normaliseAngle = (angle) => {
  const turned = ((((angle + 180) % 360) + 360) % 360) - 180;
  return Math.round((turned === -180 ? 180 : turned) * 10) / 10;
};

export const canRotate = (item) => item.type !== "arrow";

// --- 3D tilt and shadow --------------------------------------------------------
// Same maths as turn_point() and _with_shadow() in api/moodboard_render.py.

export const TILT_TYPES = ["image", "text", "swatch"];
export const MAX_TILT = 60;
// How far away the viewer stands from a tilted item, as a multiple of its longer side.
const DEPTH = 2;
// Offset and blur in percent of the board width (cqw), and how dark.
export const SHADOWS = {
  soft: { offset: 0.25, blur: 0.9, darkness: 0.22 },
  medium: { offset: 0.6, blur: 1.8, darkness: 0.28 },
  strong: { offset: 1.2, blur: 3.2, darkness: 0.34 },
};

export const canTilt = (item) => TILT_TYPES.includes(item.type);

const anglesOf = (item) => ({
  rotation: canRotate(item) ? item.rotation || 0 : 0,
  tiltX: canTilt(item) ? item.tilt_x || 0 : 0,
  tiltY: canTilt(item) ? item.tilt_y || 0 : 0,
});

// Camera distance in board-width units, or 0 when the item lies flat.
const depthOf = (item, ratio) => {
  const { tiltX, tiltY } = anglesOf(item);
  return tiltX || tiltY ? DEPTH * Math.max(item.w, item.h / ratio) : 0;
};

export function itemTransform(item, ratio) {
  const { rotation, tiltX, tiltY } = anglesOf(item);
  const parts = [];
  const depth = depthOf(item, ratio);
  if (depth) parts.push(`perspective(${depth}cqw)`);
  if (rotation) parts.push(`rotate(${rotation}deg)`);
  if (tiltX) parts.push(`rotateX(${tiltX}deg)`);
  if (tiltY) parts.push(`rotateY(${tiltY}deg)`);
  return parts.length ? parts.join(" ") : undefined;
}

// Cast straight down onto the board, after the item is turned and tilted.
export function shadowFilter(item) {
  const preset = canTilt(item) && SHADOWS[item.shadow];
  if (!preset) return undefined;
  return `drop-shadow(0 ${preset.offset}cqw ${preset.blur}cqw rgba(0, 0, 0, ${preset.darkness}))`;
}

// A point on the item, relative to its centre in board-width units, to where it shows.
function turnPoint(x, y, item, depth) {
  const { rotation, tiltX, tiltY } = anglesOf(item);
  const [a, b, t] = [tiltX, tiltY, rotation].map((angle) => (angle * Math.PI) / 180);
  const x1 = x * Math.cos(b);
  const z1 = -x * Math.sin(b);
  const y2 = y * Math.cos(a) - z1 * Math.sin(a);
  const z2 = y * Math.sin(a) + z1 * Math.cos(a);
  const x3 = x1 * Math.cos(t) - y2 * Math.sin(t);
  const y3 = x1 * Math.sin(t) + y2 * Math.cos(t);
  const k = depth ? depth / (depth - z2) : 1;
  return [x3 * k, y3 * k];
}

// Where the item's corners show on the board, in board percent, clockwise from top-left.
export function projectedCorners(item, ratio) {
  const w = item.w / 2;
  const h = item.h / ratio / 2;
  const cx = item.x + item.w / 2;
  const cy = (item.y + item.h / 2) / ratio;
  const depth = depthOf(item, ratio);
  return [
    [-w, -h],
    [w, -h],
    [w, h],
    [-w, h],
  ].map(([x, y]) => {
    const [px, py] = turnPoint(x, y, item, depth);
    return [cx + px, (cy + py) * ratio];
  });
}

const isFlat = (item) => {
  const { rotation, tiltX, tiltY } = anglesOf(item);
  return !rotation && !tiltX && !tiltY;
};

// The board-percent box a turned or tilted item actually covers.
export function visualBox(item, ratio) {
  if (isFlat(item)) return { x: item.x, y: item.y, w: item.w, h: item.h };
  const corners = projectedCorners(item, ratio);
  const xs = corners.map(([x]) => x);
  const ys = corners.map(([, y]) => y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

export function containsPoint(item, point, ratio) {
  if (isFlat(item)) {
    return point.x >= item.x && point.x <= item.x + item.w && point.y >= item.y && point.y <= item.y + item.h;
  }
  // Inside the (convex) outline when on the same side of every edge.
  const corners = projectedCorners(item, ratio).map(([x, y]) => [x, y / ratio]);
  const [px, py] = [point.x, point.y / ratio];
  let sign = 0;
  for (let index = 0; index < 4; index += 1) {
    const [ax, ay] = corners[index];
    const [bx, by] = corners[(index + 1) % 4];
    const cross = (bx - ax) * (py - ay) - (by - ay) * (px - ax);
    if (cross !== 0) {
      if (sign && Math.sign(cross) !== sign) return false;
      sign = Math.sign(cross);
    }
  }
  return true;
}

// Resizing a turned item along its own sides, keeping the opposite corner (or
// side) still. Works in screen pixels, where the turn is a true rotation.
export function resizeTurned(origin, handle, dxPx, dyPx, board, { keepAspect = false, minSize = 3 } = {}) {
  const angle = ((origin.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const w = (origin.w / 100) * board.width;
  const h = (origin.h / 100) * board.height;
  const cx = ((origin.x + origin.w / 2) / 100) * board.width;
  const cy = ((origin.y + origin.h / 2) / 100) * board.height;
  const sx = handle.includes("e") ? 1 : handle.includes("w") ? -1 : 0;
  const sy = handle.includes("s") ? 1 : handle.includes("n") ? -1 : 0;

  const localX = dxPx * cos + dyPx * sin;
  const localY = -dxPx * sin + dyPx * cos;
  const minW = (minSize / 100) * board.width;
  const minH = (minSize / 100) * board.height;
  let nw = sx ? Math.max(minW, w + sx * localX) : w;
  let nh = sy ? Math.max(minH, h + sy * localY) : h;
  if (keepAspect && sx && sy) {
    const scale = Math.max(nw / w, nh / h);
    nw = Math.max(minW, w * scale);
    nh = Math.max(minH, h * scale);
  }
  nw = Math.min(nw, board.width);
  nh = Math.min(nh, board.height);

  // The fixed corner, then the new centre measured from it.
  const ax = (-sx * w) / 2;
  const ay = (-sy * h) / 2;
  const anchorX = cx + ax * cos - ay * sin;
  const anchorY = cy + ax * sin + ay * cos;
  const bx = (sx * nw) / 2;
  const by = (sy * nh) / 2;
  const ncx = anchorX + bx * cos - by * sin;
  const ncy = anchorY + bx * sin + by * cos;

  const width = (nw / board.width) * 100;
  const height = (nh / board.height) * 100;
  return {
    x: Math.min(100 - width, Math.max(0, (ncx / board.width) * 100 - width / 2)),
    y: Math.min(100 - height, Math.max(0, (ncy / board.height) * 100 - height / 2)),
    w: width,
    h: height,
  };
}

// Corner radius is stored as a percentage of the item's shorter side and drawn
// in cqw (percent of the board width), matching _radius_px() in the renderer.
export const radiusCqw = (item, ratio) => ((item.radius || 0) / 100) * Math.min(item.w, item.h / ratio);

// Balanced split of a sequence into `count` rows by summed aspect ratio.
function partition(aspects, count) {
  const cumulative = [0];
  aspects.forEach((aspect) => cumulative.push(cumulative[cumulative.length - 1] + aspect));
  const total = cumulative[cumulative.length - 1];
  const rows = [];
  let start = 0;
  for (let row = 1; row < count; row += 1) {
    const target = (total * row) / count;
    let best = start + 1;
    const last = aspects.length - (count - row);
    for (let index = start + 1; index <= last; index += 1) {
      if (Math.abs(cumulative[index] - target) < Math.abs(cumulative[best] - target)) best = index;
    }
    rows.push([start, best]);
    start = best;
  }
  rows.push([start, aspects.length]);
  return rows;
}

// Re-flows every unlocked image into neat justified rows inside the area the
// images already cover, so text and swatches around them stay where they are.
export function autoArrange(items, aspect, assets) {
  const ratio = aspectRatio(aspect);
  const images = items
    .filter((item) => item.type === "image" && !item.locked)
    .sort((a, b) => a.y - b.y || a.x - b.x);
  if (images.length < 2) return null;

  let x0 = Math.min(...images.map((item) => item.x));
  let y0 = Math.min(...images.map((item) => item.y));
  let x1 = Math.max(...images.map((item) => item.x + item.w));
  let y1 = Math.max(...images.map((item) => item.y + item.h));
  if (((x1 - x0) * (y1 - y0)) / 10000 < MIN_REGION) {
    [x0, y0, x1, y1] = [MARGIN, MARGIN * ratio, 100 - MARGIN, 100 - MARGIN * ratio];
  }

  // Work in board-width units in both directions so gaps look even.
  const width = x1 - x0;
  const height = (y1 - y0) / ratio;
  const aspects = images.map((item) => {
    const asset = assets[`${item.source}:${item.ref}`];
    return asset?.width && asset?.height ? croppedAspect(item, asset.width, asset.height) : frameAspect(item, ratio);
  });

  let best = null;
  for (let count = 1; count <= images.length; count += 1) {
    const rows = partition(aspects, count);
    const heights = rows.map(([start, end]) => {
      const sum = aspects.slice(start, end).reduce((total, value) => total + value, 0);
      return (width - GAP * (end - start - 1)) / sum;
    });
    const used = heights.reduce((total, value) => total + value, 0) + GAP * (count - 1);
    const score = Math.abs(Math.log(used / height));
    if (!best || score < best.score) best = { rows, heights, score };
  }

  // Stretch rows to fill the height exactly; frames crop a little if needed.
  const stretch = (height - GAP * (best.rows.length - 1)) / best.heights.reduce((total, value) => total + value, 0);
  const placed = new Map();
  let cursorY = y0 / ratio;
  best.rows.forEach(([start, end], rowIndex) => {
    const rowHeight = best.heights[rowIndex] * stretch;
    let cursorX = x0;
    for (let index = start; index < end; index += 1) {
      const itemWidth = aspects[index] * best.heights[rowIndex];
      placed.set(images[index].id, {
        x: round(cursorX),
        y: round(cursorY * ratio),
        w: round(itemWidth),
        h: round(rowHeight * ratio),
      });
      cursorX += itemWidth + GAP;
    }
    cursorY += rowHeight + GAP;
  });

  return items.map((item) => (placed.has(item.id) ? { ...item, ...placed.get(item.id) } : item));
}

export function selectionBounds(items) {
  const x = Math.min(...items.map((item) => item.x));
  const y = Math.min(...items.map((item) => item.y));
  return {
    x,
    y,
    w: Math.max(...items.map((item) => item.x + item.w)) - x,
    h: Math.max(...items.map((item) => item.y + item.h)) - y,
  };
}

// Moves an item so its visible outline shifts by (dx, dy), keeping its frame on
// the board as the API requires.
const shifted = (item, dx, dy) => ({
  ...item,
  x: round(Math.min(100 - item.w, Math.max(0, item.x + dx))),
  y: round(Math.min(100 - item.h, Math.max(0, item.y + dy))),
});

// One item aligns to the board; several align to their combined bounds. Turned
// and tilted items line up by what is seen, not by their frame.
export function alignItems(items, ids, mode, ratio) {
  const chosen = items.filter((item) => ids.includes(item.id) && !item.locked);
  if (!chosen.length) return items;
  const seen = new Map(chosen.map((item) => [item.id, visualBox(item, ratio)]));
  const bounds = chosen.length === 1 ? { x: 0, y: 0, w: 100, h: 100 } : selectionBounds([...seen.values()]);
  const offset = (box) => {
    switch (mode) {
      case "left":
        return [bounds.x - box.x, 0];
      case "center":
        return [bounds.x + (bounds.w - box.w) / 2 - box.x, 0];
      case "right":
        return [bounds.x + bounds.w - box.w - box.x, 0];
      case "top":
        return [0, bounds.y - box.y];
      case "middle":
        return [0, bounds.y + (bounds.h - box.h) / 2 - box.y];
      default:
        return [0, bounds.y + bounds.h - box.h - box.y];
    }
  };
  return items.map((item) => (seen.has(item.id) ? shifted(item, ...offset(seen.get(item.id))) : item));
}

export function distributeItems(items, ids, axis, ratio) {
  const chosen = items.filter((item) => ids.includes(item.id) && !item.locked);
  if (chosen.length < 3) return items;
  const [pos, size] = axis === "horizontal" ? ["x", "w"] : ["y", "h"];
  const boxes = chosen.map((item) => ({ item, box: visualBox(item, ratio) })).sort((a, b) => a.box[pos] - b.box[pos]);
  const first = boxes[0].box;
  const last = boxes[boxes.length - 1].box;
  const span = last[pos] + last[size] - first[pos];
  const gap = (span - boxes.reduce((total, entry) => total + entry.box[size], 0)) / (boxes.length - 1);

  const placed = new Map();
  let cursor = first[pos];
  boxes.forEach(({ item, box }) => {
    const delta = cursor - box[pos];
    placed.set(item.id, axis === "horizontal" ? shifted(item, delta, 0) : shifted(item, 0, delta));
    cursor += box[size] + gap;
  });
  return items.map((item) => placed.get(item.id) || item);
}

// --- pencil, arrow and box -------------------------------------------------------

// Smallest box a drawing may have, in percent of the board.
const MIN_BOX = 1;
// Pencil points closer than this to the simplified line are dropped (board-width %).
const SIMPLIFY_TOLERANCE = 0.08;
const MAX_POINTS = 1000;

// Ramer-Douglas-Peucker: keeps the shape of a pencil stroke with far fewer points.
export function simplify(points, tolerance = SIMPLIFY_TOLERANCE) {
  if (points.length <= 2) return points;
  const keep = new Array(points.length).fill(false);
  keep[0] = keep[points.length - 1] = true;
  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [start, end] = stack.pop();
    const [ax, ay] = points[start];
    const [bx, by] = points[end];
    const length = Math.hypot(bx - ax, by - ay) || 1e-9;
    let farthest = -1;
    let distance = 0;
    for (let index = start + 1; index < end; index += 1) {
      const [px, py] = points[index];
      const d = Math.abs((bx - ax) * (ay - py) - (ax - px) * (by - ay)) / length;
      if (d > distance) {
        distance = d;
        farthest = index;
      }
    }
    if (distance > tolerance && farthest > 0) {
      keep[farthest] = true;
      stack.push([start, farthest], [farthest, end]);
    }
  }
  return points.filter((_, index) => keep[index]);
}

// Board-percent points -> an item box plus points relative to that box (0..1).
export function boxFromPoints(points, ratio) {
  const units = points.map(([x, y]) => [x, y / ratio]);
  const kept = simplify(units).slice(0, MAX_POINTS);
  const xs = kept.map(([x]) => x);
  const ys = kept.map(([, y]) => y * ratio);
  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
  let w = Math.max(...xs) - Math.min(...xs);
  let h = Math.max(...ys) - Math.min(...ys);
  const cx = (Math.max(...xs) + Math.min(...xs)) / 2;
  const cy = (Math.max(...ys) + Math.min(...ys)) / 2;
  w = Math.max(w, MIN_BOX);
  h = Math.max(h, MIN_BOX);
  const x = clamp(cx - w / 2, 0, 100 - w);
  const y = clamp(cy - h / 2, 0, 100 - h);
  const unit = (value) => Math.round(clamp(value, 0, 1) * 10000) / 10000;
  return {
    x: round(x),
    y: round(y),
    w: round(w),
    h: round(h),
    points: kept.map(([px, py]) => [unit((px - x) / w), unit((py * ratio - y) / h)]),
  };
}

// An item's points back in board percent.
export const pointsOnBoard = (item) => item.points.map(([px, py]) => [item.x + px * item.w, item.y + py * item.h]);
